import { http } from "@/lib/http";
import { isMockEnabled } from "@/lib/mock-flags";
import * as mock from "../mocks/fixtures";
import type { AgencyProfile } from "../mocks/fixtures";
import { getSeatSummary } from "@/lib/mocks/agencySeats";
import {
  getCurrentPlanMock,
  getPlanCatalogMock,
  setCurrentPlanMock,
  type PlanOption,
} from "@/lib/mocks/agencyPlan";
import { randomDelay } from "@/lib/utils/sleep";

export const getAgencyProfile = async (): Promise<AgencyProfile> => {
  if (isMockEnabled("agency")) return mock.getAgencyProfile();
  const { data } = await http.get<AgencyProfile>("/settings/profile");
  return data;
};

export const updateAgencyProfile = async (
  data: Partial<AgencyProfile>,
): Promise<AgencyProfile> => {
  if (isMockEnabled("agency")) return mock.updateAgencyProfile(data);
  const { data: updated } = await http.put<AgencyProfile>(
    "/settings/profile",
    data,
  );
  return updated;
};

// --- Plano ativo ---

export interface AgencyPlanFeature {
  label: string;
  included: boolean;
}

export interface AgencyPlan {
  code: string;
  name: string;
  /** active | trial | past_due | canceled */
  status: "active" | "trial" | "past_due" | "canceled";
  priceCents: number;
  /** monthly | yearly */
  billingCycle: "monthly" | "yearly";
  seatsTotal: number;
  seatsUsed: number;
  seatsAvailable: number;
  renewsAt: string;
  features: AgencyPlanFeature[];
}

/** Opção de plano do catálogo (modal "Gerenciar plano"). */
export interface AgencyPlanOption {
  code: string;
  name: string;
  priceCents: number;
  billingCycle: "monthly" | "yearly";
  seatsTotal: number;
  highlight: boolean;
  features: AgencyPlanFeature[];
}

// Plano ativo = plano contratado (catálogo) + seats EM USO do roster real da imobiliária.
function toActivePlan(plan: PlanOption): AgencyPlan {
  const seats = getSeatSummary();
  return {
    code: plan.code,
    name: plan.name,
    status: "active",
    priceCents: plan.priceCents,
    billingCycle: plan.billingCycle,
    seatsTotal: plan.seatsTotal,
    seatsUsed: seats.usedSlots,
    seatsAvailable: Math.max(0, plan.seatsTotal - seats.usedSlots),
    renewsAt: seats.expiresAt,
    features: plan.features,
  };
}

export const getActivePlan = async (): Promise<AgencyPlan> => {
  if (isMockEnabled("agency")) {
    await randomDelay();
    return toActivePlan(getCurrentPlanMock());
  }
  const { data } = await http.get<AgencyPlan>("/settings/plan");
  return data;
};

export const getPlanCatalog = async (): Promise<AgencyPlanOption[]> => {
  if (isMockEnabled("agency")) {
    await randomDelay();
    return getPlanCatalogMock();
  }
  const { data } = await http.get<AgencyPlanOption[]>("/settings/plans");
  return data;
};

export const changePlan = async (planCode: string): Promise<AgencyPlan> => {
  if (isMockEnabled("agency")) {
    await randomDelay(500, 1000);
    setCurrentPlanMock(planCode);
    return toActivePlan(getCurrentPlanMock());
  }
  const { data } = await http.post<AgencyPlan>("/settings/plan", { planCode });
  return data;
};

export type { AgencyProfile } from "../mocks/fixtures";
